/* eslint-disable array-bracket-newline */
/**
 * Shows Connect button or balance
 */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Network } from '@kattana-io/common';
import { useAccount, useNetwork } from 'wagmi';
import { actionShowModal } from '../../actions/modal';
import { ModalTypes } from '../../reducers/modal';
import SideBarWallet from './SideBarWallet';
import wallet from '../../assets/images/wallet.svg';
import Image from '../../atoms/Image';
import Text from '../../atoms/Text';
import Button from '../../molecules/Button';
import { formatAddress } from '../../helpers/formatters';
import { useWallet } from '../../wallet/walletContext';
import useTokenBalance from '../../hooks/useTokenBalance';
import { getNetworkFlags } from '../../wallet/helpers';
import { getSidWalletName } from '../../helpers/sidApi';
import NumberDisplay from '../../molecules/NumberDisplay';

import './styles.scss';

const ConnectedWallet = () => {
  const { address } = useAccount();
  const { chain } = useNetwork();
  const [isSideBarWallet, setSideBarWallet] = useState(false);
  const [walletSidName, setWalletSidName] = useState<string | null>(null);
  const [network, setNetwork] = useState<Network>(Network.fromChainId(1));
  const dispatch = useDispatch();

  const {
    isConnecting,
    isSignLoading,
    onDisconnect,
    state: { error, loading, tokensBySym },
  } = useWallet();

  const nativeTokenBalance = useTokenBalance({
    address,
    chainId: network.toDecimal(),
    token: '',
    interval: 15000,
    enabled: chain?.id === network?.toDecimal(),
  });

  /**
   * Call the modal for connecting new wallet
   */
  const onConnect = () => {
    if (isConnecting) {
      onDisconnect();
    }
    dispatch(actionShowModal(ModalTypes.connect, {}));
  };

  /**
   * Change of address/chain effects
   */
  useEffect(() => {
    if (!chain) {
      return;
    }
    if (chain.id != network?.toDecimal()) {
      setNetwork(Network.fromChainId(chain.id));
    }
  }, [chain]);

  const isChainEVM = chain?.id ? getNetworkFlags(chain.id).isEVM : false;
  const isArbitrum = network.isARBITRUM();

  /**
   * Try to get SID name if we have EVM address
   */
  useEffect(() => {
    if (isChainEVM && address) {
      getSidWalletName(address, isArbitrum).then((name) =>
        setWalletSidName(name)
      );
    }
  }, [address, isChainEVM, isArbitrum]);

  if (address && network) {
    const nativeToken = network.getNativeCurrencySymbol();

    const onWalletSelectClose = () => {
      setSideBarWallet(false);
    };

    const onLogout = () => {
      onDisconnect();
    };

    return (
      <>
        <div
          className='connected_wallet'
          onClick={() => setSideBarWallet(!isSideBarWallet)}
        >
          <div className='connected_wallet__info'>
            <NumberDisplay
              value={nativeTokenBalance.toNumber()}
              endSymbol={` ${nativeToken}`}
              textProps={{ size: 14, className: 'balance' }}
            />
            <Text size={walletSidName ? 12 : 10} className='address'>
              {walletSidName || formatAddress(address)}
            </Text>
          </div>
          <div className='trigger icon-chevron-right' />
        </div>

        {isSideBarWallet && (
          <SideBarWallet
            error={error}
            loading={loading}
            mapping={tokensBySym}
            network={network}
            walletSidName={walletSidName}
            address={address}
            balance={nativeTokenBalance.toNumber()}
            nativeToken={nativeToken}
            onLogout={onLogout}
            onClose={onWalletSelectClose}
          />
        )}
      </>
    );
  }

  const contents =
    isConnecting || isSignLoading ? (
      <Text size={14}>Connecting</Text>
    ) : (
      <>
        <Image src={wallet} size='m' />
        <Text size={14}>Connect Wallet</Text>
      </>
    );

  return (
    <div className='connect_btn'>
      <Button
        action={onConnect}
        size={'s'}
        variant={'primary'}
        component={contents}
      />
    </div>
  );
};

export default ConnectedWallet;
